<template>
  <ReportFieldRow
    :label="label"
    :extraCount="extraCount"
    @add="$emit('add')"
  >
    <template v-slot:badges>
      <FilterBadge
        v-if="hasBrandFilter"
        :filter="brandFilter"
        @click="$emit('add')"
      />
      <div
        class="badge-wrapper"
        v-if="useDescriptiveBadges">
        <div
          v-for="(filter, index) in displayFilters"
          :key="'header-filter-'+index"
          class="descriptive-badge"
          @click="$emit('add')"
        >
          <span class="filter-name">{{filter.name}}</span>
          <span>{{filter.operator}}</span>
          <span class="filter-value">{{filter.firstValue}}</span>
          <span>{{filter.condition}}</span>
          <span v-if="filter.extraValues">
            <span class="filter-value">
              +{{filter.extraValues}}
            </span>
            &nbsp;others
          </span>
          <span v-else class="filter-value">
            {{filter.secondValue}}
          </span>
        </div>
      </div>
      <FilterBadge
        v-else
        v-for="filter in displayFilters"
        :key="filter.key"
        :filter="filter"
        type="dismiss"
        @click="$emit('add')"
        @dismiss="removeFilter(filter)"
      />
    </template>
  </ReportFieldRow>
</template>
<script>
import { mapGetters, mapState } from 'vuex';
import utils from '../../../services/utils';
import FilterBadge from './Badges/FilterBadge/FilterBadge.vue';
import ReportFieldRow from './ReportFieldRow.vue';
import {
  getViewSelectedFilters,
  removeBrandOnlyFilters,
  removeCategoryOnlyFilters,
  isViewFilterable,
} from '../../../services/dashboard/filtersService';
import {
  getReportFieldFromValue,
} from '../../../services/dashboard/reportFieldService';

export default {
  props: {
    label: String,
    displayLimit: {
      type: Number,
      default: 2,
      validator: val => val >= 0 && val <= 3,
    },
  },
  components: {
    FilterBadge,
    ReportFieldRow,
  },
  data() {
    return {
      advancedOperators: {
        'is': {
          label: 'is',
          typeText: 'or',
        },
        'isNot': {
          label: 'is not',
          typeText: 'or',
        },
        'contains': {
          label: 'contains',
          typeText: 'and',
        },
        'doesNotContain': {
          label: 'does not contain',
          typeText: 'and',
        },
      },
    };
  },
  computed: {
    ...mapState({
      activeView: state => state.main.activeView,
      dashboardSelectedFilters: state => state.filters.dashboardSelections.filters || [],
      allowedFilters: state => state.filters.allowedFilters,
      allReportFields: state => state.dashboard.availableReportFields.allReportFields,
      filterType: state => state.dashboard.reportRequest.demographicsFilterType,
      demographicsFilters: state => state.dashboard.reportRequest.demographicsFilters,
      activeBenchmarksView: state => state.benchmarks.activeBenchmarksView,
      showNewBrandSelector: state => state.main.showNewBrandSelector,
    }),
    ...mapGetters([
      'selectedBrands',
    ]),
    filters() {
      if (!isViewFilterable(this.activeView)) return [];

      if (this.useDescriptiveBadges) {
        return this.advancedFilters;
      }

      const filters = getViewSelectedFilters(
        this.activeView,
        this.allowedFilters,
        this.dashboardSelectedFilters,
      );

      if (this.activeView === 'Benchmarks' && this.activeBenchmarksView === 'category') {
        return removeBrandOnlyFilters(filters);
      }

      if (this.activeView === 'Benchmarks' && this.activeBenchmarksView === 'brand') {
        return removeCategoryOnlyFilters(filters);
      }

      return filters;
    },
    advancedFilters() {
      return this.reshapedSelectedFilters.reduce((groupsAcc, group) => {

        const groupToDisplay = group.reduce((filtersAcc, filter) => {
          const filterToDisplay = this.reformatFilter(filter);

          if (filterToDisplay) filtersAcc.push(filterToDisplay);

          return filtersAcc;
        }, []);

        if (groupToDisplay.length > 0) {
          groupsAcc = groupsAcc.concat(groupToDisplay);
        }

        return groupsAcc;
      }, []);
    },
    reshapedSelectedFilters() {
      return (this.demographicsFilters || []).map(filterGroup => {
        return filterGroup.filters.map(f => {
          const filterCopy = { ...f };

          filterCopy.value = [...f.value];

          return filterCopy;
        });
      });
    },
    brandFilter() {
      const selectedBrandNames = this.selectedBrands.reduce((names, { checked, key }) => {
        return checked ? names.concat(key) : names;
      }, []);

      if (!selectedBrandNames || !selectedBrandNames.length) {
        return {};
      }

      return {
        key: 'brandName',
        value: selectedBrandNames,
        operator: 'includes',
      };
    },
    hasBrandFilter() {
      return !this.showNewBrandSelector
        && this.brandFilter && !utils.isEmptyObject(this.brandFilter);
    },
    displayFilters() {
      return (this.filters || []).slice(0, this.displayLimit);
    },
    extraCount() {
      return (this.filters || []).length - this.displayLimit;
    },
    useDescriptiveBadges() {
      return this.filterType === 'Advanced' && this.activeView === 'Shopper Intelligence';
    },
  },
  methods: {
    removeFilter(filter) {
      const dashboardNewFilters = this.dashboardSelectedFilters.filter(f => f.key !== filter.key);

      this.$store.dispatch('setDashboardSelectedFilters', dashboardNewFilters);

      this.$emit('complete');
    },
    reformatFilter(filter) {
      if (filter && filter.key && filter.operator && filter.value &&
        filter.value.length && this.advancedOperators[filter.operator]) {
        const dim = getReportFieldFromValue(this.allReportFields, filter.key);

        return {
          name: dim.key,
          operator: this.advancedOperators[filter.operator].label,
          firstValue: filter.value[0],
          secondValue: filter.value[1] ? filter.value[1] : null,
          condition: filter.value[1] ? this.advancedOperators[filter.operator].typeText : null,
          extraValues: filter.value.length > 2 ? filter.value.length - 1 : 0,
        };
      }

      return null;
    },
  },
};
</script>
<style scoped>
.badge-wrapper {
  display: flex;
  align-self: baseline;
}
.descriptive-badge {
  border: 1px solid #C1CCD4;
  border-radius: 4px;
  font-family: Lato;
  font-style: Regular;
  font-size: 14px;
  line-height: 18px;
  font-size: 14px;
  font-weight: 400;
  padding: 4px 6px;
  margin: 0px 4px;
  cursor: pointer;
}
.filter-name {
  color: #4369F4;;
}
.filter-value {
  color: #B545AA;
}
</style>
